import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  X,
  Download,
  ZoomIn,
  ZoomOut,
  Maximize2,
  FileText,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

interface InvoicePreviewProps {
  invoice: any;
  onClose: () => void;
}

export default function InvoicePreview({ invoice, onClose }: InvoicePreviewProps) {
  const [zoom, setZoom] = useState(100);
  const [fullscreen, setFullscreen] = useState(false);
  const [rotation, setRotation] = useState(0);

  const fileType = invoice.fileType || 'application/pdf';
  const isPDF = fileType === 'application/pdf';
  const isImage = fileType.startsWith('image/');

  // Construct data URL from base64
  const fileDataURL = `data:${fileType};base64,${invoice.fileData}`;

  const handleDownload = () => {
    try {
      const link = document.createElement('a');
      link.href = fileDataURL;
      link.download = invoice.fileName || `invoice-${invoice.invoiceNumber}.${isPDF ? 'pdf' : 'jpg'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download invoice');
    }
  };

  const handlePrint = () => {
    try {
      const printWindow = window.open(fileDataURL, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      toast.success('Opening print dialog...');
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to open print dialog');
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  return (
    <div className={`fixed inset-0 z-50 bg-black/90 flex flex-col ${fullscreen ? '' : 'p-4'}`}>
      {/* Header */}
      <div className="bg-gray-900 text-white p-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <FileText className="h-6 w-6" />
          <div>
            <h2 className="font-semibold">{invoice.fileName || 'Invoice Preview'}</h2>
            <p className="text-sm text-gray-400">
              Invoice #{invoice.invoiceNumber} • ${invoice.amount?.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Zoom Controls */}
          {isImage && (
            <>
              <Button
                onClick={handleZoomOut}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-400 min-w-[4rem] text-center">
                {zoom}%
              </span>
              <Button
                onClick={handleZoomIn}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <div className="w-px h-6 bg-gray-700 mx-2" />
            </>
          )}

          {/* Rotate (images only) */}
          {isImage && (
            <>
              <Button
                onClick={handleRotate}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <div className="w-px h-6 bg-gray-700 mx-2" />
            </>
          )}

          {/* Fullscreen Toggle */}
          <Button
            onClick={() => setFullscreen(!fullscreen)}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>

          {/* Download */}
          <Button
            onClick={handleDownload}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>

          {/* Print */}
          <Button
            onClick={handlePrint}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
          >
            Print
          </Button>

          {/* Close */}
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-auto flex items-center justify-center p-4">
        {isPDF ? (
          <div className="w-full h-full bg-white rounded-lg shadow-2xl overflow-hidden">
            <iframe
              src={fileDataURL}
              className="w-full h-full"
              title="Invoice PDF"
              style={{ border: 'none' }}
            />
          </div>
        ) : isImage ? (
          <div className="flex items-center justify-center">
            <img
              src={fileDataURL}
              alt={invoice.fileName}
              className="max-w-full max-h-full object-contain shadow-2xl rounded-lg"
              style={{
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                transition: 'transform 0.3s ease',
              }}
            />
          </div>
        ) : (
          <Card className="p-8 text-center">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-700 mb-4">
              Preview not available for this file type
            </p>
            <Button onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download File
            </Button>
          </Card>
        )}
      </div>

      {/* Footer Info */}
      <div className="bg-gray-900 text-white p-3 text-sm flex items-center justify-between">
        <div className="flex items-center space-x-4 text-gray-400">
          <span>Size: {(invoice.fileSize / 1024).toFixed(2)} KB</span>
          <span>Type: {fileType}</span>
          <span>Uploaded: {new Date(invoice.uploadedAt).toLocaleDateString()}</span>
        </div>
        <div className="text-gray-400">
          Provider: {invoice.uploadedBy}
        </div>
      </div>
    </div>
  );
}
